import React, { useRef, KeyboardEvent } from "react";

import { CustomDropdown } from "@/modules/common/components/ui/custom-drop-down";
import { muiTheme } from "@/styles/theme";
import { Box, Button, TextField, Typography } from "@mui/material";
import { Filter } from "lucide-react";
import DateRangePicker from "@/modules/common/components/ui/date-picker/date-range-picker";
import MultiSelectCheckboxFilter from "@/modules/common/components/input/dropdowns/multi-select-checkbox-list";
import { useFilterRenderer } from "@/modules/common/hooks/common-table.hook/common-table-filter-renderer.hook";
import { CommonTableColumns } from "@/modules/common/types";
import { MultiAutoCompleteSelectChip } from "@/modules/common/components/input/dropdowns/multi-auto-complete";
import { SingleAutoCompleteSelectChip } from "@/modules/common/components/input/single-autocomplete-select";
import DisabledFieldWrapper from "@/modules/common/components/filters/disabled-field-wrapper";
import { useDisabledFields } from "@/modules/common/context/disabled-fields.context";

const FilterRenderer: React.FC<{
  column: CommonTableColumns;
  isLoadingFilter?: boolean;
  filters?: any;
  setFilters?: (filters: any) => void;
  zoomLevel?: number;
  setIsTableChange?: (isTableChange: boolean) => void;
  clearMultiselectValues?: boolean;
}> = ({
  column,
  isLoadingFilter,
  filters,
  setFilters,
  zoomLevel,
  setIsTableChange,
  clearMultiselectValues,
}) => {
  const disabledFieldKeys = useDisabledFields();

  const {
    filterValue,
    filterValueTo,
    startDate,
    endDate,
    multiSelectValues,
    fetchedOptions,
    screen,
    isFilterActive,
    clearCurrentFilter,
    onFilterChange,
    onFilterChangeTo,
    handleDateRangeChange,
    handleMultiSelectChange,
    applyFilter,
    setStartDate,
    setEndDate,
    setMultiSelectValues,
    handleRemoveMultiSelect,
    handleAutoCompleteChange,
  } = useFilterRenderer(
    column,
    isLoadingFilter,
    filters,
    setFilters,
    setIsTableChange,
    clearMultiselectValues
  );

  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const numericSearchKeys = [
    "bedrooms",
    "bathrooms",
    "floorNumber",
    "unitArea",
    "roofArea",
    "isUnderConstruction",
    "gardenArea",
    "telephone",
  ];

  const triggerButton = (
    handleClick: (event: React.MouseEvent<HTMLElement>) => void
  ) => (
    <Box
      sx={{
        color: muiTheme.colorSchemes.light.palette.main.white,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "4px",
        borderRadius: "4px",
        backgroundColor: isFilterActive()
          ? muiTheme.colorSchemes.light.palette.warning[600]
          : "transparent",
        ":hover": {
          backgroundColor: isFilterActive()
            ? muiTheme.colorSchemes.light.palette.warning[700]
            : muiTheme.colorSchemes.light.palette.primary[100],
        },
        ":active": {
          color: muiTheme.colorSchemes.light.palette.primary[700],
        },
        transition: "color 0.2s, background-color 0.2s",
      }}
      onClick={handleClick}
    >
      <Filter size={16} />
    </Box>
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      searchButtonRef.current?.click();
    }
  };

  const renderFilterContent = () => {
    switch (column.filter) {
      case "select":
        return (
          <SingleAutoCompleteSelectChip
            options={column?.options || fetchedOptions}
            name="filter"
            label="Filter"
            onChange={onFilterChange}
            value={filterValue as string | number}
            onInputChange={(value) => column?.setSearchTerm?.(value)}
            autoFocus={true}
            zoomLevel={zoomLevel}
          />
        );
      case "text":
        return (
          <TextField
            fullWidth
            variant="outlined"
            type={
              numericSearchKeys.includes(column.filterKey || "")
                ? "number"
                : "text"
            }
            autoFocus
            size="small"
            label="Search"
            value={filterValue || ""}
            onChange={(e) => onFilterChange(e.target.value)}
            onKeyDown={(e) => {
              if (numericSearchKeys.includes(column.filterKey || "")) {
                const invalidChars = ["e", "E", "+", "-"];
                if (invalidChars.includes(e.key)) {
                  e.preventDefault();
                }
              }
            }}
          />
        );
      case "date":
        return (
          <DateRangePicker
            label="Date"
            startDate={startDate as Date}
            endDate={endDate as Date}
            handleChange={handleDateRangeChange}
            handleClear={() => {
              setStartDate(null);
              setEndDate(null);
            }}
            defaultValue={
              (startDate &&
                endDate &&
                `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`) ||
              ""
            }
          />
        );

      case "multiSelect":
        return (
          <MultiSelectCheckboxFilter
            options={column?.options || fetchedOptions}
            onChange={handleMultiSelectChange}
            selectedValues={multiSelectValues}
            setSelectedValues={setMultiSelectValues}
          />
        );

      case "autoComplete":
        return (
          <MultiAutoCompleteSelectChip
            options={column?.options || fetchedOptions}
            name="filter"
            label="Filter"
            onChange={handleAutoCompleteChange}
            onRemove={handleRemoveMultiSelect}
            value={
              (column.filterKey &&
              Array.isArray(filters[column.filterKey]) &&
              typeof filters[column.filterKey][0] === "string"
                ? filters[column.filterKey][0].split(",")
                : []) || []
            }
            autoFocus={true}
            zoomLevel={zoomLevel}
          />
        );

      case "textRange":
        return (
          <>
            <TextField
              fullWidth
              variant="outlined"
              autoFocus
              size="small"
              label="From"
              value={
                filterValue && !isNaN(parseFloat(filterValue as string))
                  ? Number(filterValue).toLocaleString("en-US")
                  : ""
              }
              onChange={(e) => {
                const numericValue = e.target.value.replace(/,/g, "");
                onFilterChange(numericValue);
              }}
            />
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="To"
              value={
                filterValueTo && !isNaN(parseFloat(filterValueTo as string))
                  ? Number(filterValueTo).toLocaleString("en-US")
                  : ""
              }
              onChange={(e) => {
                const numericValue = e.target.value.replace(/,/g, "");
                onFilterChangeTo(numericValue);
              }}
            />
          </>
        );
      default:
        return null;
    }
  };

  if (column?.filter) {
    return (
      <Box
        ml={1}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <CustomDropdown
          triggerButton={triggerButton}
          zoomLevel={zoomLevel}
          width={screen.isMobile ? "270px" : "310px"}
          items={[
            {
              jsx: (
                <Box
                  sx={{
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    maxHeight: "400px",
                    overflowY: "auto",
                  }}
                  onKeyDown={handleKeyDown}
                >
                  <Typography variant="h5_med">{column.Header}</Typography>
                  {column.filterKey && (
                    <DisabledFieldWrapper fieldKey={column.filterKey}>
                      {renderFilterContent()}
                    </DisabledFieldWrapper>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexDirection: "row-reverse",
                      gap: "16px",
                    }}
                  >
                    <Button
                      ref={searchButtonRef}
                      variant="contained"
                      sx={{
                        backgroundColor:
                          muiTheme.colorSchemes.light.palette.primary[500],
                        textTransform: "capitalize",
                        color: muiTheme.colorSchemes.light.palette.main.white,
                        "&:hover": {
                          backgroundColor:
                            muiTheme.colorSchemes.light.palette.primary[600],
                        },
                        width: "50%",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        applyFilter();
                      }}
                      disabled={
                        isLoadingFilter ||
                        ((!filterValue ||
                          (Array.isArray(filterValue) &&
                            filterValue.length > 0 &&
                            !filterValue[0]?.toString().trim())) &&
                          (!filterValueTo ||
                            (Array.isArray(filterValueTo) &&
                              filterValueTo.length > 0 &&
                              !filterValueTo[0]?.toString().trim())) &&
                          multiSelectValues.length === 0 &&
                          !startDate &&
                          !endDate)
                      }
                    >
                      Search
                    </Button>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor:
                          muiTheme.colorSchemes.light.palette.main.white,
                        color: muiTheme.colorSchemes.light.palette.warning[600],
                        textTransform: "capitalize",
                        "&:hover": {
                          backgroundColor:
                            muiTheme.colorSchemes.light.palette.neutral[100],
                        },
                        width: "50%",
                      }}
                      disabled={
                        !!(
                          disabledFieldKeys &&
                          column.filterKey &&
                          disabledFieldKeys.has(column.filterKey)
                        )
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        clearCurrentFilter();
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              ),
            },
          ]}
        />
      </Box>
    );
  }

  return null;
};

export default FilterRenderer;
